import { useState, useRef, useEffect } from 'react';
import { MoreVertical, Share2, Pencil, Pin, Archive, Trash2, X } from 'lucide-react';

export const ChatMenu = ({ chat, onShare, onRename, onPin, onArchive, onDelete, onRenamingChange }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [newName, setNewName] = useState(chat.title);

  const setRenaming = (val) => { setIsRenaming(val); onRenamingChange?.(val); };
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);

  const MENU_HEIGHT = 180; // approximate height of the dropdown

  const handleOpenMenu = (e) => {
    e.stopPropagation();
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const top =
        spaceBelow < MENU_HEIGHT
          ? rect.top + window.scrollY - MENU_HEIGHT - 4  // flip upward
          : rect.bottom + window.scrollY + 4;             // open downward
      setMenuPosition({
        top,
        left: Math.min(
          rect.right - 160,
          window.innerWidth - 168, // prevent right overflow
        ),
      });
    }
    setShowMenu(true);
  };

  const handleCloseMenu = () => {
    setShowMenu(false);
  };

  const handleRename = () => {
    if (newName.trim() && newName !== chat.title) {
      onRename(chat.conversationId || chat.ConversationId, newName);
    }
    setRenaming(false);
    setShowMenu(false);
  };

  // Close menu on escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        setShowMenu(false);
        setIsRenaming(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);



  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={handleOpenMenu}
        className="opacity-0 group-hover:opacity-100 p-1 rounded-md hover:bg-slate-200 dark:hover:bg-slate-600 transition-all"
      >
        <MoreVertical size={14} />
      </button>

      {/* Dropdown Menu */}
      {showMenu && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[100]"
            onClick={handleCloseMenu}
          />
          
          {/* Menu  */}
          <div 
            className="chat-menu-dropdown fixed z-[9999] min-w-[160px] bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 py-1 animate-in fade-in zoom-in-95 duration-100"
            style={{ top: menuPosition.top, left: menuPosition.left }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                onShare?.(chat);
                handleCloseMenu();
              }}
              className="w-full px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 transition-colors"
            >
              <Share2 size={14} />
              Share
            </button>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                setRenaming(true);
                handleCloseMenu();
              }}
              className="w-full px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 transition-colors"
            >
              <Pencil size={14} />
              Rename
            </button>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                onPin?.(chat.conversationId || chat.ConversationId);
                handleCloseMenu();
              }}
              className="w-full px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 transition-colors"
            >
              <Pin size={14} />
              {chat.isPinned ? 'Unpin Chat' : 'Pin Chat'}
            </button>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                onArchive?.(chat.conversationId || chat.ConversationId);
                handleCloseMenu();
              }}
              className="w-full px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 transition-colors"
            >
              <Archive size={14} />
              Archive
            </button>
            
            <div className="border-t border-slate-100 dark:border-slate-700 my-1"></div>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.(chat.conversationId || chat.ConversationId);
                handleCloseMenu();
              }}
              className="w-full px-3 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 transition-colors"
            >
              <Trash2 size={14} />
              Delete
            </button>
          </div>
        </>
      )}

      {/* Rename Modal */}
      {isRenaming && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 w-96 shadow-xl animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                Rename Chat
              </h3>
              <button
                onClick={() => setRenaming(false)}
                className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <X size={18} />
              </button>
            </div>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#1d57a5] mb-4"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleRename()}
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setRenaming(false)}
                className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
              >
                Cancel
              </button>
              <button
                onClick={handleRename}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-[#1d57a5] text-white hover:bg-[#164280]"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};